import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PropertyFilterField } from '@/components/filters/PropertyFilterField'
import { GroupTypeFilterField } from '@/components/filters/GroupTypeFilterField'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { scenariosApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/base-client'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useHeaderExtra } from '@/contexts/header.context'
import {
  OfficeHoursMode,
  RelationshipAccessLevel,
  SCENARIO_TYPE_VALUES,
  getScenarioTemplateById,
  getOfficeHoursFromFilterConditions,
  getPropertyGroupIdsFromFilterConditions,
  getPropertyIdsFromFilterConditions,
  getGroupTypesFromFilterConditions,
  buildFilterConditions,
} from '@super/shared-types'
import type { ScenarioType } from '@super/shared-types'

const CUSTOM_TYPE = 'CUSTOM'
type ScenarioTypeOption = ScenarioType | typeof CUSTOM_TYPE

const SCENARIO_TYPE_OPTIONS: { value: ScenarioTypeOption; label: string }[] = [
  { value: 'EMERGENCY', label: 'Emergency' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'LEASING', label: 'Leasing' },
  { value: CUSTOM_TYPE, label: 'Custom' },
]

const OFFICE_HOURS_OPTIONS: { value: OfficeHoursMode; label: string }[] = [
  { value: OfficeHoursMode.ALWAYS, label: 'Always' },
  { value: OfficeHoursMode.BUSINESS_HOURS, label: 'Business hours' },
  { value: OfficeHoursMode.AFTER_HOURS, label: 'After business hours' },
]

const VALID_TYPE_OPTIONS: string[] = [...SCENARIO_TYPE_VALUES, CUSTOM_TYPE]

const defineScenarioSchema = z.object({
  type: z
    .string()
    .refine(v => v === '' || VALID_TYPE_OPTIONS.includes(v), 'Invalid type'),
  name: z.string().min(1, 'Name is required').max(255),
  selectionCriteria: z.string(),
  officeHours: z.nativeEnum(OfficeHoursMode).optional(),
  propertyIds: z.array(z.string()),
  propertyGroupIds: z.array(z.string()),
  groupTypes: z.array(z.nativeEnum(RelationshipAccessLevel)),
})

type DefineScenarioFormValues = z.infer<typeof defineScenarioSchema>

const defaultValues: DefineScenarioFormValues = {
  type: '',
  name: '',
  selectionCriteria: '',
  officeHours: undefined,
  propertyIds: [],
  propertyGroupIds: [],
  groupTypes: [],
}

function payloadTypeFromForm(formValue: string): ScenarioType | null {
  if (!formValue || formValue === CUSTOM_TYPE) return null
  if ((SCENARIO_TYPE_VALUES as readonly string[]).includes(formValue)) {
    return formValue as ScenarioType
  }
  return null
}

function dropdownValueFromScenario(
  scenarioType: ScenarioType | null | undefined,
): ScenarioTypeOption {
  // Existing scenarios with a null type hydrate as "Custom" so the dropdown
  // shows a value matching what the user originally chose (or what the
  // scenario was migrated as). On create we keep `defaultValues.type = ''`
  // so the placeholder is shown instead.
  if (!scenarioType) return CUSTOM_TYPE
  return scenarioType
}

interface ConflictInfo {
  id: string
  name: string
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function extractConflict(err: unknown): ConflictInfo | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null
  const body = err.body
  if (!isObject(body) || !('conflict' in body)) return null
  const conflict = body.conflict
  if (!isObject(conflict)) return null
  const { id, name } = conflict
  if (typeof id !== 'string' || typeof name !== 'string') return null
  return { id, name }
}

export function ScenarioDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id
  useDocumentHead({
    title: isNew ? 'New Scenario' : 'Edit Scenario',
    description: 'Define scenario and variables to extract',
  })

  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [searchParams] = useSearchParams()
  // Template chosen in the "Create scenario" modal on the list page; seeds the
  // form on create. Absent (or unknown) means start from scratch.
  const templateId = searchParams.get('template')
  const [loading, setLoading] = useState(!isNew)
  const [isSaving, setIsSaving] = useState(false)
  const [conflict, setConflict] = useState<ConflictInfo | null>(null)

  const form = useForm<DefineScenarioFormValues>({
    resolver: zodResolver(defineScenarioSchema),
    defaultValues,
  })

  // Watch every field that participates in the (type, filterConditions)
  // composite uniqueness rule plus selectionCriteria (which gates whether
  // the rule fires at all). Editing any of these can resolve a stale 409,
  // so we clear the banner the moment they change.
  const watchedType = form.watch('type')
  const watchedSelectionCriteria = form.watch('selectionCriteria')
  const watchedOfficeHours = form.watch('officeHours')
  const watchedPropertyIds = form.watch('propertyIds')
  const watchedGroupTypes = form.watch('groupTypes')
  useEffect(() => {
    if (conflict) setConflict(null)
  }, [
    watchedType,
    watchedSelectionCriteria,
    watchedOfficeHours,
    watchedPropertyIds,
    watchedGroupTypes,
  ])

  useHeaderExtra(
    <div className="flex items-center">
      <Link
        to={ROUTE_REGISTRY.SCENARIOS.path}
        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md hover:bg-accent transition-colors text-sm text-muted-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to scenarios
      </Link>
    </div>,
    [],
  )

  useEffect(() => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }

    if (isNew) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    const fetchScenario = isSuperAdmin
      ? scenariosApiClient.getScenarioForCompanyByAdmin(id!, currentCompany.id)
      : scenariosApiClient.getScenarioById(id!)

    fetchScenario
      .then(scenario => {
        if (cancelled || !scenario) return
        form.reset({
          type: dropdownValueFromScenario(scenario.type),
          name: scenario.name,
          selectionCriteria: scenario.selectionCriteria,
          officeHours: getOfficeHoursFromFilterConditions(
            scenario.filterConditions,
          ),
          propertyIds: getPropertyIdsFromFilterConditions(
            scenario.filterConditions,
          ),
          propertyGroupIds: getPropertyGroupIdsFromFilterConditions(
            scenario.filterConditions,
          ),
          groupTypes: getGroupTypesFromFilterConditions(
            scenario.filterConditions,
          ),
        })
      })
      .catch(() => toast.error('Failed to load scenario'))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, isNew, currentCompany?.id, isSuperAdmin, form])

  // On create, seed the form from the template chosen in the modal. Templates
  // are static starting points — they only pre-fill the form; the saved
  // scenario owns its own config from then on.
  useEffect(() => {
    if (!isNew || !templateId) return
    const template = getScenarioTemplateById(templateId)
    if (!template) return
    form.reset({
      ...defaultValues,
      type: template.defaultSettings.type ?? CUSTOM_TYPE,
      name: template.defaultSettings.name,
      selectionCriteria: template.defaultSettings.selectionCriteria,
      officeHours: template.defaultSettings.officeHours,
    })
    // Seed once from the initial template param; the user owns the form after.
  }, [isNew, templateId, form])

  const onSubmit = async (values: DefineScenarioFormValues) => {
    if (!currentCompany?.id) return
    setIsSaving(true)
    setConflict(null)
    const filterConditions = buildFilterConditions({
      officeHours: values.officeHours,
      propertyIds: values.propertyIds,
      propertyGroupIds: values.propertyGroupIds,
      groupTypes: values.groupTypes,
    })
    const payload = {
      type: payloadTypeFromForm(values.type),
      name: values.name,
      selectionCriteria: values.selectionCriteria,
      filterConditions: filterConditions ?? [],
    }
    try {
      if (isNew) {
        const created = isSuperAdmin
          ? await scenariosApiClient.createScenarioForCompany(
              currentCompany.id,
              payload,
            )
          : await scenariosApiClient.createScenario(payload)
        if (created) {
          toast.success('Scenario created')
          navigate(pathFor('SCENARIOS'), { replace: true })
        } else {
          toast.error('Failed to create scenario')
        }
      } else {
        const updated = isSuperAdmin
          ? await scenariosApiClient.updateScenarioForCompany(
              id!,
              currentCompany.id,
              payload,
            )
          : await scenariosApiClient.updateScenario(id!, payload)
        if (updated) {
          toast.success('Scenario updated')
          navigate(pathFor('SCENARIOS'), { replace: true })
        } else {
          toast.error('Failed to update scenario')
        }
      }
    } catch (err) {
      const conflictInfo = extractConflict(err)
      if (conflictInfo) {
        setConflict(conflictInfo)
      } else {
        toast.error('Failed to save scenario')
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-y-auto pt-16">
        <div className="p-6">
          <p className="text-sm text-muted-foreground">Loading scenario…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24 max-w-2xl mt-6">
        <PageHeader title={isNew ? 'Create Scenario' : 'Edit Scenario'} />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Info</CardTitle>
                <CardDescription>
                  Pick a system type or choose Custom for company-defined
                  scenarios.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="max-w-xs">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SCENARIO_TYPE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Leasing for 100 Main"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Conditions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="officeHours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>When</FormLabel>
                      <Select
                        value={field.value ?? ''}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Always" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {OFFICE_HOURS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <PropertyFilterField
                  name="propertyIds"
                  groupsName="propertyGroupIds"
                />
                <GroupTypeFilterField name="groupTypes" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Classification</CardTitle>
                <CardDescription>
                  This description is used to identify a scenario
                  mid-conversation.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="selectionCriteria"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Scenario</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="e.g. Emergency maintenance, plumbing, HVAC, or other property emergency"
                          className="min-h-[100px] text-sm"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {conflict && (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
              >
                These conditions are already used by{' '}
                <Link
                  to={pathFor('SCENARIOS_DETAIL', { id: conflict.id })}
                  className="font-medium underline"
                >
                  {conflict.name}
                </Link>
                . Adjust the conditions or update that scenario instead.
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(ROUTE_REGISTRY.SCENARIOS.path)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving…' : isNew ? 'Save' : 'Update'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
